// Component to ensure user profile exists in the database
import { useEffect, useState, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useUser } from '@/lib/hooks'
import { Loader2 } from 'lucide-react'

export default function UserProfileCheck({ children }: { children: ReactNode }) {
  const { profile: user } = useUser()
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    if (!user) {
      setChecking(false)
      return
    }

    const checkProfile = async () => {
      try {
        // Wait a moment for the database trigger to create the profile
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Check if user profile exists
        const { data: profile } = await supabase
          .from('users')
          .select('id')
          .eq('id', user.id)
          .maybeSingle()

        if (!profile) {
          // Profile doesn't exist yet, wait a bit more for the trigger
          await new Promise(resolve => setTimeout(resolve, 2000))
          
          // Check one more time
          const { data: retryProfile } = await supabase
            .from('users')
            .select('id')
            .eq('id', user.id)
            .maybeSingle()
          
          if (!retryProfile) {
            console.warn('User profile not created by database trigger')
          }
        }
      } catch (err) {
        console.error('Error checking profile:', err)
      } finally {
        setChecking(false)
      }
    }

    checkProfile()
  }, [user])

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-600">Setting up your profile...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}